<script>
  import { _, user } from "../../stores/index"
  import { onMount } from "svelte"
  import { AppsRepository } from "../../repositories/index"
  import { ConfirmModal, AppModal, Layout } from "../../components/index"
  import { Link, navigate } from "svelte-routing"
  import { faPlus, faPen, faTrashAlt } from "@fortawesome/pro-regular-svg-icons"
  import {
    SimpleDataTable,
    DataCell,
    Flex,
    FlexItem,
    Button,
    Icon,
    Text,
    Loader,
    DataTableExpansion,
    DataTableExpansionContent,
    Select,
    SelectItem,
    TextField,
  } from "@gl/design-system"
  import { faSort, faSortDown, faSortUp } from "@fortawesome/pro-solid-svg-icons"
  import { format } from "date-fns"

  let apps = []
  let loading = {}
  let modal
  let modalType

  let confirmModal
  let selectedApp

  let search = { type: "all", name: "" }
  let availableTypes = [
    "gl-access-manager",
    "gl-compositor",
    "gl-graphics-manager",
    "gl-channel-manager",
    "gl-content-manager",
  ]
  let searched

  const order = {
    asc: true,
  }

  const handleDelete = async () => {
    try {
      loading.delete = true
      await AppsRepository.deleteApplication(selectedApp.id)
    } finally {
      fetchApps()
      loading.delete = true
    }
  }

  const fetchApps = async () => {
    try {
      loading.apps = true
      const result = await AppsRepository.getApplications()
      apps = result.list

      const appsContracts = await Promise.all(apps.map((app) => AppsRepository.getContracts(app.id)))
      apps = apps.map((app, index) => ({ ...app, contracts: appsContracts[index].list.length }))

      searched = apps
    } finally {
      loading.apps = false
    }
  }

  const handleSearch = () => {
    searched = apps
      .filter((app) => app.type === search.type || search.type === "all")
      .filter((app) => app.name.toUpperCase().includes(search.name.trim().toUpperCase()))
  }

  const handleReset = () => {
    search = { type: "all", name: "" }
    handleSearch()
  }

  function sort(field = null, toggleAsc = true) {
    order.asc = !order.asc
    order.by = field

    const cmpA = order.asc ? -1 : 1
    const cmpB = order.asc ? 1 : -1
    searched = searched.sort((a, b) => {
      const A = field === "modified" ? a.metadata.modifiedAt : a[order.by].toUpperCase()
      const B = field === "modified" ? b.metadata.modifiedAt : b[order.by].toUpperCase()
      return A === B ? 0 : A < B ? cmpA : cmpB
    })
    order.icon = order.asc ? "ArrowDown" : "ArrowUp"
  }

  onMount(() => {
    fetchApps()
    if (!$user.data.isMaster) {
      navigate("/forbidden")
    }
  })
</script>

{#if modal}
  <AppModal
    bind:active={modal}
    on:close={() => {
      modal = false
      selectedApp = null
    }}
    {modalType}
    data={selectedApp}
    on:success={() => {
      selectedApp = null
      modal = false
      fetchApps()
    }}
  />
{/if}

{#if confirmModal}
  <ConfirmModal
    bind:active={confirmModal}
    on:close={() => {
      confirmModal = false
      selectedApp = null
    }}
    handleYes={handleDelete}
    title={$_("apps.delete")}
    confirmationValue={selectedApp.id}
  >
    <p>{$_("apps.delete.warning1")}<b>{selectedApp.name}</b> ?</p>
    <p>{$_("apps.delete.warning2")}</p>

    <p class="mt-4 mb-2">
      {$_("apps.delete.warning.confirmation1")}
      <b>{selectedApp.id}</b>
      {$_("apps.delete.warning.confirmation2")}
    </p>
  </ConfirmModal>
{/if}

<Layout selected="globalApps">
  {#if loading.apps}
    <Loader />
  {:else}
    <Flex>
      <FlexItem>
        <h3 class="level-3 gl-title">{$_("common.apps")}</h3>
      </FlexItem>

      <FlexItem pushEnd>
        <Button
          on:click={() => {
            modal = true
            modalType = "post"
          }}
        >
          <Icon icon={faPlus} />
          <Text>{$_("apps.add_app")}</Text>
        </Button>
      </FlexItem>
    </Flex>

    <p class="mb-5">
      {$_("apps.total", {
        values: { total: apps.length },
      })}
    </p>
    <Flex class="mb-5 align-items-center">
      <FlexItem class="mr-4">
        <Select bind:value={search.type} id="group">
          <SelectItem label={`${$_("common.type")}: ${$_("common.all")}`} value="all" />
          {#each availableTypes as type}
            <SelectItem label={`${$_("common.type")}: ${type}`} value={type} />
          {/each}
        </Select>
      </FlexItem>

      <FlexItem style="width:300px" class="mr-4">
        <TextField
          variant="Search"
          bind:value={search.name}
          on:keydown={(e) => {
            if (e.key === "Enter") {
              handleSearch()
            }
          }}
        />
      </FlexItem>

      <FlexItem class="mr-4">
        <Button on:click={handleSearch}>
          {$_("common.search")}
        </Button>
      </FlexItem>

      <FlexItem>
        <Button outlined on:click={handleReset}>
          {$_("common.reset")}
        </Button>
      </FlexItem>
    </Flex>

    <div class="table-container">
      <SimpleDataTable
        let:index
        let:row
        data={searched || apps}
        fullWidth
        selectable={false}
        loading={loading.apps}
        variant="separate"
        hasExpansions
        stickyHead
      >
        <slot name="empty" slot="empty">
          <div class="gl-message" style="text-align: center">{$_("common.no.display")}</div>
        </slot>
        <DataCell
          {index}
          id={`${row.id}-id`}
          header="ID"
          headerIcon={order.by === "id" && order.asc ? faSortUp : order.by === "id" && !order.asc ? faSortDown : faSort}
          clickableHeader
          activeHeader
          on:clickHeader={() => sort("id")}
          maxWidth={300}
        >
          <Link to={`admin/apps/${row.id}/contracts`}>{row.id}</Link>
        </DataCell>

        <DataCell {index} header={$_("common.name")} id={`${row.id}-name`} maxWidth={300}>
          {row.name}
        </DataCell>

        <DataCell {index} header={$_("common.type")} id={`${row.id}-type`} maxWidth={300}>
          {row.type ?? "-"}
        </DataCell>

        <DataCell {index} header={$_("apps.policy.definition")} id={`${row.id}-policy`}>
          {row.policyDefinition}
        </DataCell>

        <DataCell {index} header={$_("admin.apps.contracts")} id={`${row.id}-policy`}>
          {row.contracts}
        </DataCell>

        <DataCell
          {index}
          id={`${row.id}-modified`}
          header={$_("common.last.modified")}
          headerIcon={order.by === "modified" && order.asc
            ? faSortUp
            : order.by === "modified" && !order.asc
            ? faSortDown
            : faSort}
          clickableHeader
          activeHeader
          on:clickHeader={() => sort("modified")}
        >
          {row.metadata?.modifiedAt ? format(new Date(row.metadata.modifiedAt), "dd.MM.yyyy HH:mm:ss") : "-"}
          {#if row.metadata?.modifiedBy}
            <p class="text-light" style="font-size:14px">
              {$_("common.by")}
              {row.metadata.modifiedBy}
            </p>
          {/if}
        </DataCell>

        <DataTableExpansion slot="expansion" let:row>
          {#if row.isExpanded}
            <DataTableExpansionContent colspan={7}>
              <Flex class="justify-content-end">
                <FlexItem>
                  <Button
                    outlined
                    class="mr-1  mb-1"
                    size="tiny"
                    on:click={() => {
                      modal = true
                      modalType = "put"
                      selectedApp = row
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("buttons.data")}</Text>
                  </Button>

                  <Button
                    outlined
                    class="mr-1 mb-1"
                    size="tiny"
                    on:click={() => {
                      navigate(`apps/${row.id}/contracts`)
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.contracts")}</Text>
                  </Button>

                  <Button
                    outlined
                    class="mr-1 mb-1"
                    size="tiny"
                    on:click={() => {
                      navigate(`apps/${row.id}/policies`)
                    }}
                  >
                    <Icon icon={faPen} />
                    <Text>{$_("common.policies")}</Text>
                  </Button>

                  <Button
                    outlined
                    size="tiny"
                    status="error"
                    on:click={() => {
                      selectedApp = row
                      confirmModal = true
                    }}
                  >
                    <Icon icon={faTrashAlt} />
                    <Text>{$_("buttons.delete")}</Text>
                  </Button>
                </FlexItem>
              </Flex>
            </DataTableExpansionContent>
          {/if}
        </DataTableExpansion>
      </SimpleDataTable>
    </div>
  {/if}
</Layout>
